import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  fetchDashboardSummaryAPI,
  fetchSalesTrendAPI,
  fetchStockSummaryAPI,
  fetchTopProductsAPI,
  fetchExpenseSummaryAPI,
  fetchExpenseTrendAPI,
} from './dashboardAPI';
import {
  DashboardSummary,
  SalesTrendItem,
  StockSummary,
  TopProduct,
  ExpenseSummaryData,
  ExpenseTrendItem,
  DashboardPeriod,
} from '../../types/dashboard';

// ===============================
// STATE INTERFACE
// ===============================

interface DashboardState {
  summary: DashboardSummary | null;
  salesTrend: SalesTrendItem[];
  expenseTrend: ExpenseTrendItem[];
  stockSummary: StockSummary | null;
  topProducts: TopProduct[];
  expenseSummary: ExpenseSummaryData | null;
  selectedPeriod: DashboardPeriod;
  customStartDate: string;
  customEndDate: string;
  loading: {
    summary: boolean;
    salesTrend: boolean;
    expenseTrend: boolean;
    stock: boolean;
    topProducts: boolean;
    expenses: boolean;
  };
  error: string | null;
}

const initialState: DashboardState = {
  summary: null,
  salesTrend: [],
  expenseTrend: [],
  stockSummary: null,
  topProducts: [],
  expenseSummary: null,
  selectedPeriod: 'today',
  customStartDate: '',
  customEndDate: '',
  loading: {
    summary: false,
    salesTrend: false,
    expenseTrend: false,
    stock: false,
    topProducts: false,
    expenses: false,
  },
  error: null,
};

// ===============================
// ASYNC THUNKS
// ===============================

export const fetchDashboardSummary = createAsyncThunk(
  'dashboard/fetchSummary',
  async (params: { startDate?: string; endDate?: string; storeId?: string } = {}) => {
    const response = await fetchDashboardSummaryAPI(params);
    return response;
  }
);

export const fetchSalesTrend = createAsyncThunk(
  'dashboard/fetchSalesTrend',
  async (params: { startDate?: string; endDate?: string; storeId?: string; tz?: number } = {}) => {
    const response = await fetchSalesTrendAPI(params);
    return response;
  }
);

export const fetchStockSummaryData = createAsyncThunk(
  'dashboard/fetchStockSummary',
  async (params: { storeId?: string } = {}) => {
    const response = await fetchStockSummaryAPI(params);
    return response;
  }
);

export const fetchTopProductsData = createAsyncThunk(
  'dashboard/fetchTopProducts',
  async (params: { startDate?: string; endDate?: string; limit?: number; storeId?: string } = {}) => {
    const response = await fetchTopProductsAPI(params);
    return response;
  }
);

export const fetchDashboardExpenses = createAsyncThunk(
  'dashboard/fetchExpenses',
  async (params: { startDate?: string; endDate?: string; storeId?: string } = {}) => {
    const response = await fetchExpenseSummaryAPI(params);
    return response;
  }
);

export const fetchExpenseTrendData = createAsyncThunk(
  'dashboard/fetchExpenseTrend',
  async (params: { startDate?: string; endDate?: string; tz?: number } = {}) => {
    const response = await fetchExpenseTrendAPI(params);
    return response;
  }
);

// ===============================
// SLICE
// ===============================

const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    setSelectedPeriod: (state, action: PayloadAction<DashboardPeriod>) => {
      state.selectedPeriod = action.payload;
    },
    setCustomDateRange: (state, action: PayloadAction<{ startDate: string; endDate: string }>) => {
      state.customStartDate = action.payload.startDate;
      state.customEndDate = action.payload.endDate;
    },
    clearDashboardError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Dashboard Summary
    builder
      .addCase(fetchDashboardSummary.pending, (state) => {
        state.loading.summary = true;
        state.error = null;
      })
      .addCase(fetchDashboardSummary.fulfilled, (state, action) => {
        state.loading.summary = false;
        state.summary = action.payload?.data || null;
      })
      .addCase(fetchDashboardSummary.rejected, (state, action) => {
        state.loading.summary = false;
        state.error = action.error?.message || 'Failed to fetch dashboard summary';
      });

    // Sales Trend
    builder
      .addCase(fetchSalesTrend.pending, (state) => {
        state.loading.salesTrend = true;
      })
      .addCase(fetchSalesTrend.fulfilled, (state, action) => {
        state.loading.salesTrend = false;
        state.salesTrend = action.payload?.data || [];
      })
      .addCase(fetchSalesTrend.rejected, (state, action) => {
        state.loading.salesTrend = false;
        state.error = action.error?.message || 'Failed to fetch sales trend';
      });

    // Stock Summary
    builder
      .addCase(fetchStockSummaryData.pending, (state) => {
        state.loading.stock = true;
      })
      .addCase(fetchStockSummaryData.fulfilled, (state, action) => {
        state.loading.stock = false;
        state.stockSummary = action.payload?.data || null;
      })
      .addCase(fetchStockSummaryData.rejected, (state, action) => {
        state.loading.stock = false;
        state.error = action.error?.message || 'Failed to fetch stock summary';
      });

    // Top Products
    builder
      .addCase(fetchTopProductsData.pending, (state) => {
        state.loading.topProducts = true;
      })
      .addCase(fetchTopProductsData.fulfilled, (state, action) => {
        state.loading.topProducts = false;
        state.topProducts = action.payload?.data || [];
      })
      .addCase(fetchTopProductsData.rejected, (state, action) => {
        state.loading.topProducts = false;
        state.error = action.error?.message || 'Failed to fetch top products';
      });

    // Expenses
    builder
      .addCase(fetchDashboardExpenses.pending, (state) => {
        state.loading.expenses = true;
      })
      .addCase(fetchDashboardExpenses.fulfilled, (state, action) => {
        state.loading.expenses = false;
        state.expenseSummary = action.payload?.data || null;
      })
      .addCase(fetchDashboardExpenses.rejected, (state, action) => {
        state.loading.expenses = false;
        state.error = action.error?.message || 'Failed to fetch expenses';
      });

    // Expense Trend
    builder
      .addCase(fetchExpenseTrendData.pending, (state) => {
        state.loading.expenseTrend = true;
      })
      .addCase(fetchExpenseTrendData.fulfilled, (state, action) => {
        state.loading.expenseTrend = false;
        state.expenseTrend = action.payload?.data || [];
      })
      .addCase(fetchExpenseTrendData.rejected, (state, action) => {
        state.loading.expenseTrend = false;
        state.error = action.error?.message || 'Failed to fetch expense trend';
      });
  },
});

export const { setSelectedPeriod, setCustomDateRange, clearDashboardError } = dashboardSlice.actions;

// Selectors
export const selectDashboardSummary = (state: { dashboard: DashboardState }) => state.dashboard.summary;
export const selectSalesTrend = (state: { dashboard: DashboardState }) => state.dashboard.salesTrend;
export const selectExpenseTrend = (state: { dashboard: DashboardState }) => state.dashboard.expenseTrend;
export const selectStockSummary = (state: { dashboard: DashboardState }) => state.dashboard.stockSummary;
export const selectTopProducts = (state: { dashboard: DashboardState }) => state.dashboard.topProducts;
export const selectExpenseSummary = (state: { dashboard: DashboardState }) => state.dashboard.expenseSummary;
export const selectDashboardLoading = (state: { dashboard: DashboardState }) => state.dashboard.loading;
export const selectSelectedPeriod = (state: { dashboard: DashboardState }) => state.dashboard.selectedPeriod;
export const selectDashboardError = (state: { dashboard: DashboardState }) => state.dashboard.error;

export default dashboardSlice.reducer;
